import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { getDonationMethods } from '../../../_helpers/bandUtils.js';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { richMessage, withStrong } from '../../../_helpers/richMessage.jsx';
import '../../../styles/partial/modal/TipModal.css';

const PRESET_AMOUNTS = [5, 10, 20, 50, 100];
const MIN_TIP_AMOUNT = 1;
const MAX_TIP_AMOUNT = 9999;

/** Dollars with up to two decimal places: "15", "2.5", "2.50". */
const TIP_AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

/**
 * Read a typed custom amount (ENG-502). Tips may include cents; the range is
 * $1 to $9,999. Returns the amount in dollars, or the catalog key of the
 * reason it can't be used. An empty field is neither.
 * @param {string} raw
 * @returns {{amount: number|null, error: string|null}}
 */
export const parseTipAmount = (raw) => {
  const text = String(raw ?? '').trim();
  if (text === '') return { amount: null, error: null };
  if (!TIP_AMOUNT_PATTERN.test(text)) return { amount: null, error: 'tip.customInvalid' };
  const cents = Math.round(parseFloat(text) * 100);
  if (cents < MIN_TIP_AMOUNT * 100 || cents > MAX_TIP_AMOUNT * 100) return { amount: null, error: 'tip.customRange' };
  return { amount: cents / 100, error: null };
};

/** "15" for whole dollars, "2.50" otherwise. */
export const formatTipAmount = (amount) => (Number.isInteger(amount) ? String(amount) : Number(amount).toFixed(2));

/**
 * The link that hands a tip to an unverified provider (PayPal, Venmo, Cash
 * App), and whether it carries the amount. Unreachable in production for
 * now: no backend supplies the method values it needs (see
 * getDonationMethods); kept for the planned PayPal tip path (ENG-583). A band's value is a handle or a
 * full URL; a URL of a known shape (paypal.me/name, venmo.com/name or
 * venmo.com/u/name, cash.app/$name) is rebuilt with the amount, and any other
 * URL is opened as it is, so the dialog has to show the amount instead
 * (ENG-502). Zelle has no link.
 * @param {{key: string, value: string|boolean}} method
 * @param {number} amount - dollars
 * @returns {{url: string, carriesAmount: boolean}|null}
 */
export const buildTipLink = (method, amount) => {
  const value = typeof method?.value === 'string' ? method.value.trim() : '';
  if (!value) return null;
  const amt = formatTipAmount(amount);

  if (!/^https?:\/\//i.test(value)) {
    switch (method.key) {
      case 'paypal':
        return { url: `https://paypal.me/${encodeURIComponent(value)}/${amt}`, carriesAmount: true };
      case 'venmo':
        return { url: `https://venmo.com/${encodeURIComponent(value.replace(/^@/, ''))}?txn=pay&amount=${amt}&note=Tip`, carriesAmount: true };
      case 'cashapp':
        return { url: `https://cash.app/${encodeURIComponent(value.startsWith('$') ? value : `$${value}`)}/${amt}`, carriesAmount: true };
      default:
        return null;
    }
  }

  let parsed;
  try {
    parsed = new URL(value);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  const segments = parsed.pathname.split('/').filter(Boolean);
  if (method.key === 'paypal' && host === 'paypal.me' && segments[0]) {
    return { url: `https://paypal.me/${segments[0]}/${amt}`, carriesAmount: true };
  }
  if (method.key === 'venmo' && host === 'venmo.com') {
    const name = segments[0] === 'u' ? segments[1] : segments[0];
    if (name) return { url: `https://venmo.com/${name}?txn=pay&amount=${amt}&note=Tip`, carriesAmount: true };
  }
  if (method.key === 'cashapp' && host === 'cash.app' && segments[0]?.startsWith('$')) {
    return { url: `https://cash.app/${segments[0]}/${amt}`, carriesAmount: true };
  }
  return { url: value, carriesAmount: false };
};

const TipModal = ({ isOpen, onClose, bandInfo, bandId, fetchWrapper, apiBaseUrl, initialAmount }) => {
  const { t, locale } = useLocale();
  const [step, setStep] = useState('amount');
  const [selectedAmount, setSelectedAmount] = useState(initialAmount && PRESET_AMOUNTS.includes(initialAmount) ? initialAmount : null);
  const [customAmount, setCustomAmount] = useState(initialAmount && !PRESET_AMOUNTS.includes(initialAmount) ? String(initialAmount) : '');
  const [isCustom, setIsCustom] = useState(Boolean(initialAmount && !PRESET_AMOUNTS.includes(initialAmount)));
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // The link a PayPal, Venmo or Cash App tip was handed to; see buildTipLink.
  const [handoffLink, setHandoffLink] = useState(null);
  // What had focus when the dialog opened, to hand it back on close.
  const openerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep('amount');
    setSelectedAmount(initialAmount && PRESET_AMOUNTS.includes(initialAmount) ? initialAmount : null);
    setCustomAmount(initialAmount && !PRESET_AMOUNTS.includes(initialAmount) ? String(initialAmount) : '');
    setIsCustom(Boolean(initialAmount && !PRESET_AMOUNTS.includes(initialAmount)));
    setSelectedMethod(null);
    setIsSubmitting(false);
    setSubmitError(null);
    setHandoffLink(null);
  }, [isOpen, initialAmount]);

  // Against the real `GET /public/bands/{id}` (ENG-572) this is always []:
  // the response has `hasPaymentProvider` (Stripe connected) and
  // `paymentProviders` (names only), never the per-method fields
  // getDonationMethods reads. So in production the dialog goes amount ->
  // confirm -> Stripe Checkout, and the method, handoff and Zelle steps below
  // are unreachable until a backend supplies those fields (a PayPal tip path
  // is planned post-Alpha, ENG-583). They are kept, and covered by unit tests
  // and e2e stubs only.
  const donationMethods = React.useMemo(() => getDonationMethods(bandInfo, t), [bandInfo, t]);

  const hasPaymentProvider =
    Boolean(bandInfo?.hasPaymentProvider ?? bandInfo?.has_payment_provider) ||
    donationMethods.some((m) => m.key === 'stripe');

  const customParse = parseTipAmount(customAmount);
  const resolvedAmount = isCustom ? customParse.amount || 0 : selectedAmount;

  const noPaymentConfigured = donationMethods.length === 0 && !hasPaymentProvider;

  const handleClose = useCallback(() => {
    setStep('amount');
    setSelectedAmount(null);
    setCustomAmount('');
    setIsCustom(false);
    setSelectedMethod(null);
    setIsSubmitting(false);
    setSubmitError(null);
    setHandoffLink(null);
    onClose();
  }, [onClose]);

  // Escape closes the dialog, and closing hands focus back to whatever opened
  // it (ENG-502). The listener is on window in the capture phase so it runs
  // before, and stops, the page's own Escape handling (the watch page leaves
  // boost pick mode on Escape).
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;
  useEffect(() => {
    if (!isOpen) return undefined;
    openerRef.current = document.activeElement;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      handleCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      const opener = openerRef.current;
      openerRef.current = null;
      if (opener && opener !== document.body && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    };
  }, [isOpen]);

  const handlePresetClick = useCallback((amount) => {
    setSelectedAmount(amount);
    setIsCustom(false);
    setCustomAmount('');
  }, []);

  const handleCustomClick = useCallback(() => {
    setIsCustom(true);
    setSelectedAmount(null);
  }, []);

  const handleAmountContinue = useCallback(() => {
    const amount = isCustom ? parseTipAmount(customAmount).amount : selectedAmount;
    if (!amount || amount <= 0) return;
    if (donationMethods.length === 1) {
      setSelectedMethod(donationMethods[0]);
      setStep('confirm');
    } else if (donationMethods.length > 1) {
      setStep('method');
    } else if (hasPaymentProvider) {
      setStep('confirm');
    }
  }, [isCustom, customAmount, selectedAmount, donationMethods, hasPaymentProvider]);

  const handleMethodSelect = useCallback((method) => {
    setSelectedMethod(method);
    setStep('confirm');
  }, []);

  /**
   * Hand the payer to Stripe Checkout (ENG-468). The backend records the tip
   * and answers with the Checkout page's URL; Stripe sends the payer back to
   * the page they tipped from, with ?tip=success or ?tip=cancelled. Returns
   * whether the redirect is under way; on failure it shows errorKey.
   */
  const startStripeCheckout = useCallback(async (amount, errorKey) => {
    try {
      const response = await fetchWrapper.post(
        `${apiBaseUrl}/public/bands/${bandId}/tips/checkout`,
        {
          amount_cents: Math.round(amount * 100),
          locale,
          return_path: window.location.pathname,
        }
      );
      if (response?.checkout_url) {
        window.location.href = response.checkout_url;
        return true;
      }
      throw new Error('No checkout URL received');
    } catch (err) {
      setSubmitError(t(errorKey));
      setIsSubmitting(false);
      return false;
    }
  }, [fetchWrapper, apiBaseUrl, bandId, locale, t]);

  const handleConfirm = useCallback(async () => {
    if (isSubmitting) return;
    const amount = resolvedAmount;
    if (!amount || amount <= 0) return;

    setIsSubmitting(true);
    setSubmitError(null);

    const method = selectedMethod;

    if (method && method.key === 'stripe' && fetchWrapper && apiBaseUrl) {
      await startStripeCheckout(amount, 'tip.stripeCheckoutError');
      return;
    }

    // PayPal, Venmo, Cash App and Zelle tell us nothing about whether money
    // moved, so the dialog says how to finish the tip there, never that it
    // happened (ENG-502). Unreachable in production: see donationMethods.
    if (method && method.key !== 'stripe') {
      if (method.key === 'zelle') {
        setStep('handoff');
        setIsSubmitting(false);
        return;
      }
      const link = buildTipLink(method, amount);
      if (link) {
        window.open(link.url, '_blank', 'noopener,noreferrer');
        setHandoffLink(link);
        setStep('handoff');
        setIsSubmitting(false);
        return;
      }
    }

    // The production path: no concrete method, Stripe connected (ENG-572).
    if (!method && hasPaymentProvider && fetchWrapper && apiBaseUrl) {
      await startStripeCheckout(amount, 'tip.checkoutError');
      return;
    }

    setIsSubmitting(false);
  }, [isSubmitting, resolvedAmount, selectedMethod, fetchWrapper, apiBaseUrl, hasPaymentProvider, startStripeCheckout]);

  if (!isOpen) return null;

  const zelleValue = donationMethods.find((m) => m.key === 'zelle')?.value;
  const titleId = 'tip-modal-title';
  const amountText = t('tip.amount', { amount: resolvedAmount ? formatTipAmount(resolvedAmount) : resolvedAmount });
  const customErrorId = 'tip-modal-custom-error';

  const modal = (
    <div
      className="tip-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={handleClose}
    >
      <div className="tip-modal-panel" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="tip-modal-close"
          aria-label={t('tip.closeLabel')}
          onClick={handleClose}
        >
          ×
        </button>

        <div className="tip-modal-header">
          <span className="tip-modal-kicker">{t('tip.kicker')}</span>
          <h2 id={titleId} className="tip-modal-title">
            {bandInfo?.name || t('tip.defaultTitle')}
          </h2>
        </div>

        {step === 'amount' && (
          <div className="tip-modal-step">
            <p className="tip-modal-copy">{t('tip.chooseAmount')}</p>
            <div className="tip-modal-amounts">
              {PRESET_AMOUNTS.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  className={`tip-modal-amount-btn${selectedAmount === amt && !isCustom ? ' active' : ''}`}
                  onClick={() => handlePresetClick(amt)}
                >
                  {t('tip.amount', { amount: amt })}
                </button>
              ))}
              <button
                type="button"
                className={`tip-modal-amount-btn${isCustom ? ' active' : ''}`}
                onClick={handleCustomClick}
              >
                {t('tip.other')}
              </button>
            </div>

            {isCustom && (
              <div className="tip-modal-custom-row">
                <span className="tip-modal-currency">{t('tip.currencySymbol')}</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="tip-modal-custom-input"
                  placeholder={t('tip.customPlaceholder')}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  autoFocus
                  aria-label={t('tip.customLabel')}
                  aria-invalid={customParse.error ? 'true' : undefined}
                  aria-describedby={customParse.error ? customErrorId : undefined}
                />
              </div>
            )}
            {isCustom && customParse.error && (
              <p id={customErrorId} className="tip-modal-field-error" role="alert">
                {t(customParse.error)}
              </p>
            )}

            <div className="tip-modal-actions">
              <button type="button" className="tip-modal-cancel" onClick={handleClose}>
                {t('common.actions.cancel')}
              </button>
              <button
                type="button"
                className="tip-modal-continue"
                disabled={!resolvedAmount || resolvedAmount < MIN_TIP_AMOUNT || resolvedAmount > MAX_TIP_AMOUNT || noPaymentConfigured}
                onClick={handleAmountContinue}
              >
                {t('tip.continue')}
              </button>
            </div>
          </div>
        )}

        {step === 'method' && (
          <div className="tip-modal-step">
            <p className="tip-modal-copy">
              {withStrong(t('tip.chooseMethod'), { amount: amountText })}
            </p>
            <div className="tip-modal-methods">
              {donationMethods.map((method) => (
                <button
                  key={method.key}
                  type="button"
                  className="tip-modal-method-btn"
                  onClick={() => handleMethodSelect(method)}
                >
                  {method.label}
                </button>
              ))}
            </div>
            <div className="tip-modal-actions">
              <button type="button" className="tip-modal-cancel" onClick={() => setStep('amount')}>
                {t('tip.back')}
              </button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="tip-modal-step">
            <p className="tip-modal-copy">
              {selectedMethod
                ? withStrong(t('tip.confirmSummaryVia', { method: selectedMethod.label }), { amount: amountText })
                : withStrong(t('tip.confirmSummary'), { amount: amountText })}
            </p>

            {selectedMethod?.key === 'zelle' && zelleValue && (
              <div className="tip-modal-zelle-info">
                <p>{t('tip.zelleSendTo')}</p>
                <strong className="tip-modal-zelle-value">{zelleValue}</strong>
              </div>
            )}

            {submitError && (
              <div className="tip-modal-error" role="alert">{submitError}</div>
            )}

            <div className="tip-modal-actions">
              <button
                type="button"
                className="tip-modal-cancel"
                onClick={() => {
                  setSubmitError(null);
                  setStep(donationMethods.length > 1 ? 'method' : 'amount');
                }}
              >
                {t('tip.back')}
              </button>
              <button
                type="button"
                className="tip-modal-submit"
                disabled={isSubmitting}
                onClick={handleConfirm}
              >
                {isSubmitting ? t('tip.processing') : selectedMethod?.key === 'zelle' ? t('tip.gotIt') : t('tip.sendTip')}
              </button>
            </div>
          </div>
        )}

        {step === 'handoff' && (
          <div className="tip-modal-step tip-modal-step--handoff">
            {selectedMethod?.key === 'zelle' ? (
              <p className="tip-modal-copy">
                {withStrong(t('tip.zelleInstructions'), { amount: amountText, recipient: zelleValue })}
              </p>
            ) : (
              <>
                <p className="tip-modal-copy">
                  {withStrong(t('tip.finishIn', { method: selectedMethod?.label }), { amount: amountText })}
                </p>
                {handoffLink && !handoffLink.carriesAmount && (
                  <>
                    <p className="tip-modal-send-amount" data-testid="tip-send-amount">
                      {withStrong(t('tip.sendAmount'), { amount: amountText })}
                    </p>
                    <p className="tip-modal-copy">{t('tip.amountNotFilled', { method: selectedMethod?.label })}</p>
                  </>
                )}
                {handoffLink && (
                  <p className="tip-modal-copy">
                    {richMessage(t('tip.tabDidNotOpen', { method: selectedMethod?.label }), {
                      link: (
                        <a href={handoffLink.url} target="_blank" rel="noopener noreferrer">
                          {t('tip.openMethod', { method: selectedMethod?.label })}
                        </a>
                      ),
                    })}
                  </p>
                )}
              </>
            )}
            <div className="tip-modal-actions tip-modal-actions--centered">
              <button type="button" className="tip-modal-submit" onClick={handleClose}>
                {t('common.actions.done')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default TipModal;
